import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import LoginPopup from "./LoginPopup";
import SignInPopup from "./SignInPopUp";
import Cookies from "js-cookie";
import MenuItem from "./MenuItem";
import { MobileNavigationMenu } from "./MenuItem";
import MoreMenu from "./MoreMenu";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Menu, Phone, X } from "lucide-react";
import NotificationBell from "./NotificationBell";

const Header = ({ onAdminLogin, onDefaultLogin, onLogout }) => {
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showSignInPopup, setShowSignInPopup] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginDefaultType, setLoginDefaultType] = useState("student");

  const navigate = useNavigate();
  const isLoggedIn = Boolean(Cookies.get("token"));
  const accountType = Cookies.get("accountType");

  const dashboardPath = useMemo(() => {
    if (!isLoggedIn) return null;
    if (accountType === "college") return "/college/dashboard";
    if (accountType === "author") return "/author/dashboard";
    if (accountType === "admin") return null;
    return "/student/dashboard";
  }, [isLoggedIn, accountType]);

  const handleLogout = () => {
    if (onLogout) onLogout();
    else {
      Cookies.remove("token");
      Cookies.remove("username");
      Cookies.remove("email");
      Cookies.remove("phoneNo");
      Cookies.remove("roles");
      Cookies.remove("accountType");
    }
  };

  const adminLoginSuccess = () => {
    setShowLoginPopup(false);
    if (onAdminLogin) onAdminLogin();
  };

  const defaultLoginSuccess = () => {
    setShowLoginPopup(false);
    if (onDefaultLogin) onDefaultLogin();
    // applyAuthSession() (called by LoginPopup/SignInPopUp before this fires) has already
    // written the fresh cookies, so read them directly here rather than trusting the
    // dashboardPath memo above, which won't recompute until the next render.
    const freshAccountType = Cookies.get("accountType");
    const landingPath =
      freshAccountType === "college"
        ? "/college/dashboard"
        : freshAccountType === "author"
          ? "/author/dashboard"
          : "/student/dashboard";
    navigate(landingPath);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-9 flex items-center text-xs sm:text-sm">
            <div className="flex items-center gap-4">
              <a
                href="tel:+919884702223"
                className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 no-underline"
              >
                <Phone className="h-4 w-4" />
                <span>+91 98847 02223</span>
              </a>
              <a
                href="mailto:info@bharatvidyarthi.com"
                className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 no-underline"
              >
                <Mail className="h-4 w-4" />
                <span className="hidden sm:inline">info@bharatvidyarthi.com</span>
                <span className="sm:hidden">Email</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-3 no-underline">
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-white flex items-center justify-center border border-gray-200">
              <img
                src="/images/bvlogo.png"
                alt="BharatVidyarthi"
                className="h-full w-full object-contain"
                loading="eager"
              />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-lg text-gray-900">BharatVidyarthi</div>
              <div className="text-xs text-gray-500">AI College Search Engine</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-3">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-gray-900 no-underline">
              Home
            </Link>
            <MenuItem />
            <Link
              to="/compare"
              className="text-sm font-medium text-gray-700 hover:text-gray-900 no-underline"
            >
              Compare
            </Link>
            <Link
              to="/admission-assistance"
              className="text-sm font-medium text-gray-700 hover:text-gray-900 no-underline"
            >
              Admission Assistance
            </Link>
            <MoreMenu />
          </nav>

          <div className="hidden lg:flex items-center gap-2 relative">
            {isLoggedIn && accountType === "student" && (
              <NotificationBell onNavigate={(link) => navigate(link)} />
            )}

            {isLoggedIn ? (
              <div className="flex items-center gap-2">
                {accountType === "admin" ? (
                  <a
                    href="/admin/jwt-login"
                    className="px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 no-underline"
                  >
                    Admin Panel
                  </a>
                ) : dashboardPath ? (
                  <Link
                    to={dashboardPath}
                    className="px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 no-underline"
                  >
                    Dashboard
                  </Link>
                ) : null}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="px-3 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-sm font-medium text-white"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowSignInPopup(true)}
                  className="px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Sign Up
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setLoginDefaultType("student");
                    setShowLoginPopup(true);
                  }}
                  className="px-3 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-sm font-medium text-white"
                >
                  Login
                </button>
              </div>
            )}
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            type="button"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={mobileMenuOpen ? "close" : "open"}
                initial={{ rotate: -45, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 45, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="block"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {mobileMenuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="lg:hidden border-t border-gray-200 bg-white overflow-hidden"
          >
          <div className="px-4 py-4 space-y-3">
            <div className="py-2">
              <MobileNavigationMenu onNavigate={() => setMobileMenuOpen(false)} />
            </div>

            <div className="py-2 space-y-3">
              <Link
                to="/compare"
                className="block text-sm font-medium text-gray-700 hover:text-gray-900 no-underline"
                onClick={() => setMobileMenuOpen(false)}
              >
                Compare
              </Link>
              <Link
                to="/admission-assistance"
                className="block text-sm font-medium text-gray-700 hover:text-gray-900 no-underline"
                onClick={() => setMobileMenuOpen(false)}
              >
                Admission Assistance
              </Link>
              <Link
                to="/study-abroad"
                className="block text-sm font-medium text-gray-700 hover:text-gray-900 no-underline"
                onClick={() => setMobileMenuOpen(false)}
              >
                Study Abroad
              </Link>
              <Link
                to="/careers"
                className="block text-sm font-medium text-gray-700 hover:text-gray-900 no-underline"
                onClick={() => setMobileMenuOpen(false)}
              >
                Careers
              </Link>
              <Link
                to="/articles"
                className="block text-sm font-medium text-gray-700 hover:text-gray-900 no-underline"
                onClick={() => setMobileMenuOpen(false)}
              >
                News Articles
              </Link>
            </div>

            {isLoggedIn && accountType === "student" && (
              <div className="flex justify-end">
                <NotificationBell
                  onNavigate={(link) => {
                    setMobileMenuOpen(false);
                    navigate(link);
                  }}
                />
              </div>
            )}

            {isLoggedIn ? (
              <div className="flex gap-2">
                {accountType === "admin" ? (
                  <a
                    href="/admin/jwt-login"
                    className="flex-1 px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 text-center no-underline"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin Panel
                  </a>
                ) : dashboardPath ? (
                  <Link
                    to={dashboardPath}
                    className="flex-1 px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 text-center no-underline"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="flex-1 px-3 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-sm font-medium text-white"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setShowSignInPopup(true);
                  }}
                  className="flex-1 px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Sign Up
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setLoginDefaultType("student");
                    setShowLoginPopup(true);
                  }}
                  className="flex-1 px-3 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-sm font-medium text-white"
                >
                  Login
                </button>
              </div>
            )}
          </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showSignInPopup && (
        <SignInPopup onClose={() => setShowSignInPopup(false)} onDefaultLogin={defaultLoginSuccess} />
      )}

      {showLoginPopup && (
        <LoginPopup
          onClose={() => setShowLoginPopup(false)}
          onAdminLogin={adminLoginSuccess}
          onDefaultLogin={defaultLoginSuccess}
          defaultAccountType={loginDefaultType}
        />
      )}
    </header>
  );
};

export default Header;
